import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

import { VenueService } from '../../shared/services/venue.service';
import { AuthService, SsoLoginOptions } from '../../shared/services/auth.service';
import { Store } from '@ngxs/store';
import { VenueState } from 'src/app/shared/store/venue/venue.state';

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy {
  public loginForm: UntypedFormGroup;
  public needToChooseVenue = false;
  public isInvalid = false;
  public errorMessage: string | null = null;
  public venueId = 0;
  public sso: SsoLoginOptions | null = null;
  public busy = false;

  private ssoLookup: Subscription;

  constructor(
    private venueService: VenueService,
    private authService: AuthService,
    private formBuilder: UntypedFormBuilder,
    private router: Router,
    private store: Store
    ) { }

  ngOnInit() {
    this.venueId = this.store.selectSnapshot(VenueState.venueId);

    if (this.authService.isAuthenticated()) {

      if (this.venueId) {
        this.router.navigate(['/discover']);
      } else {
        this.needToChooseVenue = true;

        this.authService.getUser();
      }
    }

    this.loginForm = this.formBuilder.group({
      email: ['', { validators: Validators.required}],
      password: ['', { validators: Validators.required}]
    });

    this.ssoLookup = this.formControl.email.valueChanges
      .pipe(
        debounceTime(400),
        distinctUntilChanged(),
        switchMap((email: string) => {
          const trimmed = (email || '').trim();
          return EMAIL_SHAPE.test(trimmed) ? this.authService.ssoLoginOptions(trimmed) : of(null);
        })
      )
      .subscribe((options) => this.applySsoOptions(options));
  }

  ngOnDestroy() {
    if (this.ssoLookup) {
      this.ssoLookup.unsubscribe();
    }
  }

  get formControl() { return this.loginForm.controls; }

  get ssoRequired(): boolean {
    return !!(this.sso && this.sso.ssoRequired);
  }

  get providerLabel(): string {
    return (this.sso && this.sso.providerName) || 'your organisation';
  }

  // An SSO-only domain has no password to ask for, so the field must stop being required.
  private applySsoOptions(options: SsoLoginOptions | null) {
    this.sso = options && options.ssoAvailable ? options : null;
    const password = this.formControl.password;
    if (this.ssoRequired) {
      password.clearValidators();
    } else {
      password.setValidators(Validators.required);
    }
    password.updateValueAndValidity();
  }

  startSso() {
    const url = this.authService.ssoStartUrl(this.sso ? this.sso.startUrl : null);
    if (!url) {
      this.errorMessage = 'Single sign-on is not available right now. Try again shortly.';
      return;
    }
    this.busy = true;
    window.location.assign(url);
  }

  login() {
    if (this.ssoRequired) {
      this.startSso();
      return;
    }
    if (this.loginForm.invalid) {
      return;
    }

    this.isInvalid = false;
    this.errorMessage = null;
    this.busy = true;
    this.authService.login(this.formControl.email.value, this.formControl.password.value)
      .then((result) => {
        if (result === 'redirecting') {
          return;
        }
        this.busy = false;
        this.needToChooseVenue = true;
      })
      .catch((err) => {
        this.busy = false;
        if (err && err.userFacing) {
          this.errorMessage = err.message;
        } else {
          this.isInvalid = true;
        }
      });
  }

}
